/*
 * external-checkout.js ‚Äî hands the Shopify cart to the self-hosted NMI checkout.
 *
 * WHAT IT DOES
 *   Intercepts every "checkout" intent on the storefront and, instead of
 *   letting the browser go to Shopify's native /checkout, it:
 *     1. waits for any cart update still in flight (e.g. the cart drawer's
 *        Priority Shipping toggle), so the cart it reads is the one the buyer sees,
 *     2. reads the live cart (GET /cart.js),
 *     3. navigates straight to {checkout origin}/api/start with the line items,
 *        which creates the checkout and lands the buyer on it. No overlay: the
 *        browser's own loading bar shows, as for any link. (A cart too big for
 *        a URL is POSTed to {ORIGIN}/api/sessions first instead.)
 *
 *   Shopify's cart stays the single source of truth for line items; the
 *   checkout re-prices everything server-side (Shopify prices + the CRM's
 *   quantity pricing + shipping). Client amounts are never sent or trusted.
 *
 * CONFIG
 *   The API origin comes from the `data-origin` attribute on this script's
 *   tag (Theme settings ‚Üí External checkout ‚Üí Checkout API origin), or from
 *   window.EXTERNAL_CHECKOUT_ORIGIN set before this file. If neither is set,
 *   checkout is BLOCKED with a console error ‚Äî it never silently falls
 *   through to Shopify's /checkout.
 *
 * ROBUSTNESS
 *   One delegated listener on `document` in the CAPTURE phase catches buttons
 *   and links rendered later (cart drawer refreshes, upsell blocks) as well as
 *   those in the initial page.
 *
 * PUBLIC API
 *   window.ExternalCheckout.redirect()  -> Promise<void>  (start checkout manually)
 *   window.ExternalCheckout.origin      -> string | null  (resolved API origin)
 */
(function () {
  'use strict';

  var GLOBAL_KEY = 'ExternalCheckout';
  /** Longest we wait for in-flight cart updates before reading the cart anyway. */
  var CART_SETTLE_TIMEOUT_MS = 4000;
  /** Longest /api/start URL we navigate to; bigger carts use POST /api/sessions. */
  var MAX_START_URL_LENGTH = 6000;
  var CHECKOUT_ORIGIN_KEY = 'external-checkout:checkout-origin';
  var START_ERROR_MESSAGE = 'Sorry ‚Äî we could not start checkout. Please try again in a moment.';

  // ---------------------------------------------------------------------------
  // Resolve the API origin.
  // ---------------------------------------------------------------------------
  // Trailing slashes, dots and spaces are typos ("https://api.store.com." is a
  // different host to the browser, and fails).
  function normalizeOrigin(value) {
    return value.replace(/[\s/.]+$/, '');
  }

  function resolveOrigin() {
    if (typeof window.EXTERNAL_CHECKOUT_ORIGIN === 'string' && window.EXTERNAL_CHECKOUT_ORIGIN.trim() !== '') {
      return normalizeOrigin(window.EXTERNAL_CHECKOUT_ORIGIN.trim());
    }
    var self =
      document.currentScript ||
      (function () {
        var scripts = document.getElementsByTagName('script');
        for (var i = scripts.length - 1; i >= 0; i--) {
          if ((scripts[i].getAttribute('src') || '').indexOf('external-checkout') !== -1) return scripts[i];
        }
        return null;
      })();
    if (self) {
      var attr = self.getAttribute('data-origin');
      if (attr && attr.trim() !== '') return normalizeOrigin(attr.trim());
    }
    return null;
  }

  var ORIGIN = resolveOrigin();

  function configError() {
    console.error(
      '[external-checkout] CHECKOUT API ORIGIN NOT CONFIGURED. Set Theme settings ‚Üí External checkout ‚Üí ' +
        'Checkout API origin (e.g. https://api.yourstore.com). Checkout is blocked until this is fixed.'
    );
  }

  // ---------------------------------------------------------------------------
  // In-flight cart updates. The theme adds/removes items with fetch (and a few
  // apps with XHR); a checkout click that lands mid-update would read a stale
  // cart. Count pending /cart/{add,change,update,clear} requests and let the
  // handoff wait for them to settle.
  // ---------------------------------------------------------------------------
  var pendingCartRequests = 0;
  var settleWaiters = [];

  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url || '');
  }

  function cartRequestDone() {
    pendingCartRequests = Math.max(0, pendingCartRequests - 1);
    if (pendingCartRequests === 0) {
      var waiters = settleWaiters;
      settleWaiters = [];
      for (var i = 0; i < waiters.length; i++) waiters[i]();
    }
  }

  if (typeof window.fetch === 'function') {
    var originalFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (!isCartMutation(url)) return originalFetch.apply(this, arguments);
      pendingCartRequests += 1;
      var result = originalFetch.apply(this, arguments);
      result.then(cartRequestDone, cartRequestDone);
      return result;
    };
  }

  if (window.XMLHttpRequest) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ecCart = isCartMutation(String(url));
      return originalOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__ecCart) {
        pendingCartRequests += 1;
        this.addEventListener('loadend', cartRequestDone, { once: true });
      }
      return originalSend.apply(this, arguments);
    };
  }

  function cartSettled() {
    if (pendingCartRequests === 0) return Promise.resolve();
    return new Promise(function (resolve) {
      var timer = setTimeout(resolve, CART_SETTLE_TIMEOUT_MS);
      settleWaiters.push(function () {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Speed: open the connections checkout uses before the click. Buyers go
  // straight to the checkout host, which the API names (GET
  // /api/checkout-origin). A remembered host only warms the connection: the
  // buyer is sent there only once this page has confirmed it, otherwise via
  // the API host, which redirects.
  // ---------------------------------------------------------------------------
  var checkoutOrigin = null;

  function preconnect(url, anonymous) {
    try {
      var pc = document.createElement('link');
      pc.rel = 'preconnect';
      pc.href = url;
      if (anonymous) pc.crossOrigin = 'anonymous';
      (document.head || document.documentElement).appendChild(pc);
    } catch (_e) {
      /* non-fatal */
    }
  }

  function isOrigin(value) {
    return typeof value === 'string' && /^https:\/\/[^/\s]+$/.test(value);
  }

  function warmUp() {
    // Navigations use credentialed connections; fetches to the API anonymous ones.
    preconnect(ORIGIN, false);
    preconnect(ORIGIN, true);
    try {
      var remembered = localStorage.getItem(CHECKOUT_ORIGIN_KEY);
      if (isOrigin(remembered)) preconnect(remembered, false);
    } catch (_e) {
      /* storage blocked */
    }
    fetch(ORIGIN + '/api/checkout-origin', { mode: 'cors', headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (!data || !isOrigin(data.checkout_origin)) return;
        checkoutOrigin = data.checkout_origin;
        preconnect(checkoutOrigin, false);
        try {
          localStorage.setItem(CHECKOUT_ORIGIN_KEY, checkoutOrigin);
        } catch (_e) {
          /* storage blocked */
        }
      })
      .catch(function () {
        /* the API host still works */
      });
  }

  function base64url(text) {
    var utf8 = unescape(encodeURIComponent(text));
    return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  // ---------------------------------------------------------------------------
  // Tracking glue. The pixels themselves are installed by the theme (layout/
  // theme.liquid); here we fire InitiateCheckout at the moment of handoff and
  // forward the pixels' first-party cookies so the checkout's server-side
  // events can be attributed to the ad click. Best-effort ‚Äî tracking must
  // never block checkout.
  // ---------------------------------------------------------------------------
  function getCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : undefined;
    } catch (_e) {
      return undefined;
    }
  }

  function trackInitiateCheckout(cart) {
    try {
      var items = cart.items || [];
      var data = {
        content_type: 'product',
        content_ids: items.map(function (i) {
          return String(i.variant_id);
        }),
        contents: items.map(function (i) {
          return { id: String(i.variant_id), quantity: i.quantity, item_price: (i.final_price || i.price || 0) / 100 };
        }),
        value: (cart.total_price || 0) / 100,
        currency: cart.currency || 'USD',
        num_items: cart.item_count || items.length,
      };
      if (window.TrackEvents && typeof window.TrackEvents.send === 'function') {
        window.TrackEvents.send('InitiateCheckout', data);
      }
    } catch (_e) {
      /* never block checkout on tracking */
    }
  }

  // ---------------------------------------------------------------------------
  // Core redirect flow.
  // ---------------------------------------------------------------------------
  var inFlight = false;

  /** Carts too big for a URL: create the session first, then go to it. */
  function createSessionAndGo(body) {
    return fetch(ORIGIN + '/api/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      mode: 'cors',
      body: body,
    })
      .then(function (res) {
        if (!res.ok) {
          return res.text().then(function (text) {
            throw new Error('external-checkout: POST /api/sessions responded ' + res.status + ' ' + text);
          });
        }
        return res.json();
      })
      .then(function (data) {
        if (!data || typeof data.checkout_url !== 'string' || data.checkout_url === '') {
          throw new Error('external-checkout: response missing checkout_url');
        }
        window.location.assign(data.checkout_url);
      });
  }

  function redirect() {
    if (!ORIGIN) {
      configError();
      return Promise.reject(new Error('external-checkout: API origin not configured'));
    }
    if (inFlight) return Promise.resolve();
    inFlight = true;

    return cartSettled()
      .then(function () {
        return fetch('/cart.js', { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('external-checkout: /cart.js responded ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var items = (cart && Array.isArray(cart.items) ? cart.items : []).map(function (i) {
          return { variant_id: i.variant_id, quantity: i.quantity, properties: i.properties || null };
        });
        if (items.length === 0) throw new Error('external-checkout: cart is empty');

        trackInitiateCheckout(cart);

        // Pixel cookies for server-side attribution (undefined keys are dropped
        // by JSON.stringify ‚Äî nothing is sent when absent).
        var body = JSON.stringify({
          items: items,
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
          ttp: getCookie('_ttp'),
          ttclid: getCookie('ttclid'),
        });
        var startUrl = (checkoutOrigin || ORIGIN) + '/api/start?d=' + base64url(body);
        if (startUrl.length <= MAX_START_URL_LENGTH) {
          window.location.assign(startUrl);
          return;
        }
        return createSessionAndGo(body);
      })
      .catch(function (err) {
        inFlight = false;
        console.error('[external-checkout] checkout failed:', err);
        try {
          window.alert(START_ERROR_MESSAGE);
        } catch (_e) {
          /* alert unavailable ‚Äî the console error above stands */
        }
        throw err;
      });
    // On success `inFlight` stays true: the page is navigating away and must
    // not create a second session.
  }

  // ---------------------------------------------------------------------------
  // Interception. Single delegated listener, capture phase, on `document`.
  // ---------------------------------------------------------------------------
  // /checkout, /checkouts/‚Ä¶, and market-prefixed forms like /en-ca/checkout.
  var CHECKOUT_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?checkouts?(?:[/?#]|$)/i;

  function isCheckoutUrl(url) {
    return url.host === window.location.host && CHECKOUT_PATH.test(url.pathname);
  }

  function isCheckoutHref(href) {
    if (!href) return false;
    try {
      return isCheckoutUrl(new URL(href, window.location.href));
    } catch (_e) {
      return false;
    }
  }

  function matchTrigger(target) {
    var el = target;
    while (el && el !== document && el.nodeType === 1) {
      // Cart page / cart drawer / cart notification checkout buttons.
      if (el.matches('button[name="checkout"], input[name="checkout"], #CartDrawer-Checkout')) return el;
      if (el.tagName === 'A' && isCheckoutHref(el.getAttribute('href') || '')) return el;
      el = el.parentElement;
    }
    return null;
  }

  function onClickCapture(event) {
    if (event.defaultPrevented) return;
    var trigger = matchTrigger(event.target);
    if (!trigger) return;
    if (trigger.disabled) return;
    // Intercept ANY click ‚Äî including middle/modified clicks ‚Äî so a /checkout
    // link can never open Shopify's checkout in a new tab.
    event.preventDefault();
    event.stopPropagation();
    redirect().catch(function () {
      /* already logged + alerted */
    });
  }

  function onSubmitCapture(event) {
    if (event.defaultPrevented) return;
    var form = event.target;
    if (!form || form.nodeType !== 1) return;
    var action = form.getAttribute('action') || '';
    var submitter = event.submitter || null;
    var submitterIsCheckout = submitter && submitter.matches && submitter.matches('[name="checkout"]');
    var isCartForm = /\/cart\b/.test(action) && !/\/cart\/add/.test(action);
    var hasCheckoutSubmit = !!form.querySelector('[name="checkout"]');
    // A cart form submitted by its checkout button (or by Enter with one present).
    if (submitterIsCheckout || (isCartForm && hasCheckoutSubmit && (!submitter || submitterIsCheckout))) {
      event.preventDefault();
      event.stopPropagation();
      redirect().catch(function () {
        /* handled in redirect() */
      });
    }
  }

  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('submit', onSubmitCapture, true);

  // Scripted navigations: theme or app code that does
  // `location.href = '/checkout‚Ä¶'` (often minified or obfuscated, so it can't
  // be patched in the theme). The Navigation API reports every navigation the
  // page starts, whatever triggered it, and lets us cancel it. Browsers without
  // it rely on the click/submit interception above.
  if (window.navigation && typeof window.navigation.addEventListener === 'function') {
    window.navigation.addEventListener('navigate', function (event) {
      if (!event.cancelable || event.hashChange || event.downloadRequest) return;
      var url;
      try {
        url = new URL(event.destination.url);
      } catch (_e) {
        return;
      }
      if (!isCheckoutUrl(url)) return;
      event.preventDefault();
      redirect().catch(function () {
        /* handled in redirect() */
      });
    });
  }

  // Back from the checkout restores this page from the bfcache with
  // `inFlight` still true; reset it on every show.
  window.addEventListener('pageshow', function () {
    inFlight = false;
  });

  // /api/start sends the buyer back here with ?checkout_error=1 when their
  // cart couldn't be checked out: say so once, and tidy the address bar.
  (function reportFailedStart() {
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.get('checkout_error') !== '1') return;
      url.searchParams.delete('checkout_error');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      setTimeout(function () {
        window.alert(START_ERROR_MESSAGE);
      }, 0);
    } catch (_e) {
      /* non-fatal */
    }
  })();

  // ---------------------------------------------------------------------------
  // Defense in depth: hide Shopify's dynamic/express checkout buttons (Shop
  // Pay, Apple Pay, Google Pay, PayPal, "Buy it now"). They launch Shopify's
  // own express checkout, which this script cannot intercept. Removing
  // `{{ form | payment_button }}` from the theme is the primary fix; this
  // hides any an app might inject.
  // ---------------------------------------------------------------------------
  (function hideExpressButtons() {
    if (document.getElementById('external-checkout-hide-express')) return;
    var style = document.createElement('style');
    style.id = 'external-checkout-hide-express';
    style.textContent =
      '.shopify-payment-button,.dynamic-checkout__content,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,' +
      '[data-shopify="dynamic-checkout-cart"],[data-shopify="payment-button"]{display:none !important}';
    (document.head || document.documentElement).appendChild(style);
  })();

  // ---------------------------------------------------------------------------
  // Public API.
  // ---------------------------------------------------------------------------
  window[GLOBAL_KEY] = { redirect: redirect, origin: ORIGIN };

  if (!ORIGIN) configError();
  else warmUp();
})();
